import type { Currency } from '@ps/web-core-store';
import type { Selector } from 'reselect';

import { FeatureFlag } from '@swf/core/enums';
import type { ModelConfig, RootState, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { SingleOpportunity } from '@swf/generated/types/api/sports';
import type { PlaceBetResultCode } from '@swf/sports/types/place-bet';

import effects from './effects';
import * as baseReducers from './reducers/betSlip';
import type { BetSlipState, PersistedBetSlipState } from './types/betSlip';

export const initialState: BetSlipState = {
  acceptedOdds: {},
  hasInvalidCombination: false,
  isLocked: false,
  isOpen: false,
  opportunities: {
    MULTIPLE: [],
    SINGLE: []
  },
  wallets: {},
  selections: [],
  submittedOpportunities: {
    SINGLE: [],
    MULTIPLE: []
  },
  receipt: {
    SINGLE: [],
    MULTIPLE: []
  },
  rehydrated: false,
  fetchStatus: 'READY',
  selectedPromotionPicker: undefined
};

export const rehydratedInitialState: BetSlipState = {
  ...initialState,
  rehydrated: true
};

const nonAutoDismissibleResultCodes: PlaceBetResultCode[] = [
  'HISTORICAL_BALANCE_EXCEEDED',
  'ACCOUNT_CREATED_AFTER_EVENT_START',
  'BLOCKGAMEPLAY_PARALLELVERTICAL',
  'BLOCKGAMEPLAY_TIMELIMIT',
  'FAILED_GEO_COMPLY_PENDING',
  'FAILED_GEO_COMPLY_REJECTED',
  'FAILED_GEO_COMPLY_SOFTWARE_BLOCKED',
  'FAILED_GEO_COMPLY_UNKNOWN',
  'FAILED_GEO_COMPLY_USING_PROXY'
];

const selectors = (slice: Slicer<BetSlipState>, createSelector: SelectorCreator) => ({
  acceptedOdds() {
    return slice((state) => state.acceptedOdds);
  },
  isLocked() {
    return slice((state) => state.isLocked);
  },
  isOpen() {
    return slice((state) => state.isOpen);
  },
  opportunities() {
    return slice((state) => state.opportunities);
  },
  singleOpportunities() {
    return slice((state) => state.opportunities.SINGLE);
  },
  multipleOpportunities() {
    return slice((state) => state.opportunities.MULTIPLE);
  },
  receipt() {
    return slice((state) => state.receipt);
  },
  fetchStatus() {
    return slice((state) => state.fetchStatus);
  },
  pendingSelectionCount(this: {
    selectionsCount: Selector<RootState, number>;
    singleOpportunities: Selector<RootState, SingleOpportunity[]>;
  }) {
    return createSelector(
      this.selectionsCount,
      this.singleOpportunities,
      (selectionsCount, singleOpportunities) =>
        Math.max(0, selectionsCount - singleOpportunities.length)
    );
  },
  pendingRemovalCount(this: {
    selectionsCount: Selector<RootState, number>;
    singleOpportunities: Selector<RootState, SingleOpportunity[]>;
  }) {
    return createSelector(
      this.selectionsCount,
      this.singleOpportunities,
      (selectionsCount, singleOpportunities) =>
        Math.max(0, singleOpportunities.length - selectionsCount)
    );
  },
  persistedState() {
    return slice<PersistedBetSlipState>(({ receipt, rehydrated, isLocked, ...state }) => state);
  },
  submittedOpportunities() {
    return slice((state) => state.submittedOpportunities);
  },
  selections() {
    return slice((state) => state.selections);
  },
  selectionsCount() {
    return slice((state) => state.selections.length);
  },
  wallets() {
    return slice((state) => state.wallets);
  },
  hasInvalidCombination() {
    return slice((state) => state.hasInvalidCombination);
  },
  errorCode() {
    return slice((state) => state.errorCode);
  },
  errorResponse() {
    return slice((state) => state.errorResponse);
  },
  hasRehydrated() {
    return slice((state) => state.rehydrated);
  },
  requiredPlacementCurrency() {
    return slice((state) => state.requiredPlacementCurrency);
  },
  isCurrencyRestricted(this: {
    requiredPlacementCurrency: Selector<RootState, Currency | undefined>;
  }) {
    return createSelector(
      this.requiredPlacementCurrency,
      (rootState: RootState) => select.account.getCurrency(rootState),
      (requiredPlacementCurrency, selectedAccountCurrency) =>
        requiredPlacementCurrency && selectedAccountCurrency
          ? requiredPlacementCurrency !== selectedAccountCurrency
          : false
    );
  },
  dailyMaxPayoutLimit() {
    return slice((state) => state.dailyMaxPayoutLimit);
  },
  isPromotionPickerOpen() {
    return createSelector(
      slice,
      (_rootState: RootState, opportunityId: string) => opportunityId,
      (state, opportunityId) => state.selectedPromotionPicker === opportunityId
    );
  },
  selectedPromotionPicker() {
    return slice((state) => state.selectedPromotionPicker);
  },
  hasNonAutoDismissibleBetError() {
    return slice((state) =>
      state.errorResponse?.result?.find((result) =>
        nonAutoDismissibleResultCodes.includes(result.resultCode)
      )
    );
  },
  stakeInputDecimalSeparator() {
    return createSelector(
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.currency.get(rootState),
      (locale, currency) => {
        try {
          const decimalSeparator =
            new Intl.NumberFormat(locale, { style: 'currency', currency })
              .formatToParts(1.1)
              .find(({ type }) => type === 'decimal')?.value ?? '.';
          return decimalSeparator;
        } catch {
          return '.';
        }
      }
    );
  },
  isCustomKeyboardEnabled() {
    return createSelector(
      (rootState: RootState) =>
        select.feature.check(rootState, FeatureFlag.ISPBetSlipCustomKeyboard),
      (rootState: RootState) => select.device.isPhone(rootState),
      (isFeatureFlagEnabled, isDevicePhone) => isFeatureFlagEnabled && isDevicePhone
    );
  }
});

const authUpdatePending = (state: BetSlipState): BetSlipState => ({
  ...state,
  fetchStatus: 'AUTH_UPDATE_PENDING'
});

const reducers = {
  ...baseReducers,
  'session/response': authUpdatePending,
  'session/clear': authUpdatePending
};

interface BetSlipModel extends ModelConfig<BetSlipState> {
  selectors: typeof selectors;
  reducers: typeof reducers;
  effects: typeof effects;
}

const betSlip: BetSlipModel = {
  state: { ...initialState },
  selectors,
  reducers,
  effects
};

export default betSlip;
